import { HOST_API, cookies } from '@/shared/config-global';
import { getCookies } from '@/utils/cookies';
import { isSSR } from '@/shared/utils';
import { CustomError } from '@/shared/exception';
import { redirect } from 'next/navigation';
import { ErrorResponse } from '@/app/api/types';

import { stringify } from './query-string';

// ----------------------------------------------------------------------
//
// API client for the customer PWA. Talks to the new NestJS backend
// (NEXT_PUBLIC_HOST_API → e.g. https://im-api.numenai.tech).
//
// Phase 0 → 7 simplifications:
//   - Dropped the `super-admin` request branch + INTERNAL_ADMIN creds:
//     no more frontend-held service account.
//   - Dropped `is_test` / TEST_VER : Xano-era data-source toggle.
//   - Dropped cryptr decoding of /auth/me : the backend serves plaintext
//     JSON now; the previous endpoint was a Next.js proxy that decrypted.
//
// What stayed:
//   - Bearer auth from the `access_token` cookie (still readable in SSR
//     via getCookies so server components can fetch authenticated data).
//   - Centralised error → CustomError flow with Spanish messages for
//     401 (session expired) and 429 (rate limited).
//
// What we still want to add (next iteration):
//   - Refresh-token interceptor: on 401, POST /auth/refresh, retry the
//     original request, redirect to login only if the refresh also 401s.
//
// ----------------------------------------------------------------------

type ApiRequestPath = RequestInfo | URL;
type ApiRequestInit = RequestInit & {
  params?: object;
};

class Api {
  baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  get(path: ApiRequestPath, { params, ...opts }: ApiRequestInit = {}) {
    return this.request(params ? `${path}?${stringify(params)}` : path, opts);
  }

  post(path: ApiRequestPath, { params, ...opts }: ApiRequestInit = {}) {
    return this.request(path, { method: 'POST', body: params as BodyInit, ...opts });
  }

  patch(path: ApiRequestPath, { params, ...opts }: ApiRequestInit = {}) {
    return this.request(path, { method: 'PATCH', body: params as BodyInit, ...opts });
  }

  put(path: ApiRequestPath, { params, ...opts }: ApiRequestInit = {}) {
    return this.request(path, { method: 'PUT', body: params as BodyInit, ...opts });
  }

  delete(path: ApiRequestPath, { params, ...opts }: ApiRequestInit = {}) {
    return this.request(path, { method: 'DELETE', body: params as BodyInit, ...opts });
  }

  async request(path: ApiRequestPath, { body, headers, ...opts }: ApiRequestInit = {}) {
    const isExternal =
      (path as string).includes('https://') || (path as string).includes('http://');
    const url = `${isExternal ? '' : this.baseUrl}${path}`;

    const [accessToken] = await getCookies([cookies.accessToken.name]);

    const response = await fetch(url, {
      method: opts.method,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
        ...headers,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
      ...opts,
    }).catch((error) => {
      throw new CustomError(
        'Upss, parece que tienes problemas en tu conexión, por favor reintenta',
        { code: error.code, status: 500 }
      );
    });

    if (response.ok) {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.indexOf('application/json') !== -1) {
        return response.json().catch(() => 'Sin contenido');
      }
      return response.text().catch(() => 'Sin contenido');
    }

    const error = await handleErrorResponse(response, accessToken);
    throw new CustomError(error.message, { code: error.code, status: response.status });
  }
}

const api = new Api(HOST_API);

export default api;

// ----------------------------------------------------------------------
//
// Endpoint catalogue. Kept here so the call sites stay legible — most
// services do `api.get(API_ENDPOINTS.users.byId(id))` rather than
// embed string literals.
//
// Aligned with the NestJS backend's contract; new endpoints get added
// here as services are wired up.
//
// ----------------------------------------------------------------------
// Aligned 1-to-1 with the NestJS `auth` controller. Browse the
// controller at backend/src/modules/auth/auth.controller.ts if a path
// goes stale.
export const API_ENDPOINTS = {
  auth: {
    me: '/auth/me',
    login: '/auth/login',
    refresh: '/auth/refresh',
    logout: '/auth/logout',
    register: '/auth/register',
    forgotPassword: '/auth/forgot-password',
    resetPassword: '/auth/reset-password',
    changePassword: '/auth/change-password',
    otpEmailSend: '/auth/otp/email/send',
    otpEmailVerify: '/auth/otp/email/verify',
    signupOtpSend: '/auth/signup/otp/send',
    signupOtpVerify: '/auth/signup/otp/verify',
  },
};

const handleErrorResponse = async (response: Response, accessToken?: string) => {
  // Backend returns RFC 7807 application/problem+json. Older browsers
  // / server fetches sometimes don't surface the right content-type,
  // so we fall through to .text() on parse failure.
  const res: Partial<ErrorResponse> & { message?: string } = await response
    .json()
    .catch(() => ({ message: 'Algo salió mal.' }));

  if (response.status === 401) {
    res.message = accessToken
      ? 'Su autorización ha caducado! inicie sesión nuevamente.'
      : 'Por favor ingresa primero.';
    if (isSSR()) redirect('/');
    else window.location.reload();
  }

  if (response.status === 429) {
    res.message = 'Lo sentimos! Por favor intenta nuevamente en unos minutos';
  }

  // Alta con un mail que ya tiene cuenta. El backend corta apenas conoce el
  // mail; antes dejaba avanzar y la persona hacia toda la verificacion
  // (cedula + selfie) para rebotar recien en el ultimo paso.
  if (
    response.status === 409 &&
    (res as { type?: string }).type === 'users/email-already-in-use'
  ) {
    res.message = 'Ya tenés una cuenta con este mail. Iniciá sesión en vez de registrarte.';
  }

  return res;
};
